import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    useTranslate,
    SaveButton,
    Toolbar,
    useRecordContext,
    useRedirect,
    useNotify,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, IconButton } from '@mui/material';
import { ArrowBack, AttachMoney } from '@mui/icons-material';
import { FeeStructureForm } from './components/FeeStructureForm';
import { SaveOptionsToolbar } from './components/SaveOptionsToolbar';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import { validateFeeStructure, stripDisabledConsumptionRates } from './validation';

const FEE_TEMPLATE_KNOWN_FIELDS = new Set([
    'name', 'description', 'currency', 'feeStructure', 'isActive',
]);

const mapFeeTemplateBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const feeTemplateFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return FEE_TEMPLATE_KNOWN_FIELDS.has(head)
        ? `resources.fee-templates.fields.${head}`
        : field;
};

// For now, only HKD is supported
const HKD_ONLY_CHOICES = [
    { id: 'HKD', name: 'Hong Kong Dollar (HKD)' }
];

const EditToolbar = () => {
    const redirect = useRedirect();
    const notify = useNotify();

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const record = useRecordContext();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/fee-templates')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.fee-templates.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.fee-templates.edit.title', { name: record?.name })}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.fee-templates.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const FeeTemplatesEdit = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    const notify = useNotify();
    const record = useRecordContext();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            redirect="list"
            transform={stripDisabledConsumptionRates}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.fee-templates.notifications.updated', { type: 'success' });
                    redirect('/fee-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapFeeTemplateBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(feeTemplateFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<SaveOptionsToolbar />}
                validate={validateFeeStructure}
                mode="onChange"
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {record?.isSystem && (
                        <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 } }}>
                            <Typography variant="body2">
                                {translate('resources.fee-templates.edit.systemTemplateWarning')}
                            </Typography>
                        </Alert>
                    )}

                    {/* Basic Information Card */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.fee-templates.edit.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            label={translate('resources.fee-templates.fields.name')}
                                            validate={[required()]}
                                            fullWidth
                                            helperText={translate('resources.fee-templates.edit.nameHelper')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            disabled={record?.isSystem}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="currency"
                                            label={translate('resources.fee-templates.fields.currency')}
                                            choices={HKD_ONLY_CHOICES}
                                            validate={[required()]}
                                            fullWidth
                                            helperText={translate('resources.fee-templates.edit.currencyHelper')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            disabled={record?.isSystem}
                                        />
                                    </Box>
                                </Box>

                                <TextInput
                                    source="description"
                                    label={translate('resources.fee-templates.fields.description')}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    helperText={translate('resources.fee-templates.edit.descriptionHelper')}
                                    disabled={record?.isSystem}
                                />

                                <Box sx={{ mt: 1 }}>
                                    <BooleanInput
                                        source="isActive"
                                        label={translate('resources.fee-templates.fields.active')}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Fee Structure Card */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.fee-templates.edit.feeStructure')}
                                </Typography>
                            </Box>

                            <FeeStructureForm source="feeStructure" />
                        </CardContent>
                    </Card>

                    {record?.usageCount && record.usageCount > 0 && (
                        <Alert severity="info" sx={{ mt: { xs: 2, sm: 3 } }}>
                            <Typography variant="body2">
                                {translate('resources.fee-templates.edit.usageWarning', { count: record.usageCount })}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </SimpleForm>
        </Edit>
    );
};